"use client";

import { RiCloseLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useAppBannerStore } from "@/lib/stores/app-banner-store";
import { trackAppInstallInitiated } from "@/lib/services/analytics";

function detectAndroidBrowser(): boolean {
	if (typeof window === "undefined") {
		return false;
	}

	const userAgent = window.navigator.userAgent.toLowerCase();

	// Check for Android devices only (iOS handled by Smart App Banner)
	const isAndroid = /android/.test(userAgent);

	return isAndroid;
}

export function AppDownloadBanner() {
	const [isVisible, setIsVisible] = useState(false);
	const { shouldShowBanner, dismissBanner } = useAppBannerStore();

	useEffect(() => {
		const isAndroid = detectAndroidBrowser();

		// Only show on Android devices (iOS has Smart App Banner)
		if (isAndroid && shouldShowBanner()) {
			setTimeout(() => {
				setIsVisible(true);
			}, 2000); // Show after 2 seconds to not be too aggressive
		} else {
			setIsVisible(false);
		}
	}, [shouldShowBanner]);

	const handleDismiss = () => {
		dismissBanner();
		setIsVisible(false);
	};

	const handleDownloadClick = () => {
		// Track app install initiated event
		trackAppInstallInitiated("android_banner");
		handleDismiss();
	};

	if (!isVisible) return null;

	// Android Play Store link
	const androidAppUrl = "https://play.google.com/store/apps/details?id=com.humbleos.mobile&hl=en_US";

	return (
		<div className="fixed inset-x-0 bottom-0 z-50 w-full border-t bg-background pb-safe">
			<div className="relative">
				<button
					type="button"
					onClick={handleDismiss}
					className="absolute top-3 right-3 rounded-full p-1.5 hover:bg-muted transition-colors z-10"
					aria-label="Dismiss"
				>
					<RiCloseLine className="h-5 w-5 text-muted-foreground" />
				</button>

				<div className="flex items-start gap-4 p-4 pr-12">
					<div className="h-14 w-14 overflow-hidden rounded-xl shadow-lg flex-shrink-0">
						<Image
							src="/images/logos/humble-logo-brand.png"
							alt="Humble"
							width={56}
							height={56}
							className="h-full w-full object-cover"
							priority
						/>
					</div>

					<div className="flex-1 min-w-0">
						<h3 className="font-semibold text-base">Get the Humble app</h3>
						<p className="mt-0.5 text-muted-foreground text-sm line-clamp-2">
							Track festivals, save sets, and share reviews with friends
						</p>

						<Link
							href={androidAppUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="mt-3 inline-block"
							onClick={handleDownloadClick}
						>
							<Button size="sm" className="h-9 px-5">
								Download
							</Button>
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
}
